import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { PageHeader, GhostButton, PillBadge } from "../components/Primitives";
import { fmtDateTime } from "../lib/tz";
import { useTheme } from "../contexts/ThemeContext";
import {
  ShieldAlert, RotateCw, Users, Wifi, MailCheck, KeyRound, Ban, User,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";

const RANGES = [
  { key: "7d",  label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
];

/**
 * Security Overview — admin snapshot of the `security_events` audit log.
 *
 * Surfaces the four numbers a security-conscious ops lead cares about:
 * failed logins, brute-force lockouts, password-reset volume, and the top
 * source IPs / targeted accounts. Plus a stacked bar chart so patterns
 * (attacks concentrated in the last hour vs. steady over a week) are
 * obvious at a glance.
 */
export default function SecurityDashboard() {
  const [range, setRange]     = useState("7d");
  const [data, setData]       = useState(null);
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme() || { theme: "dark" };

  const tooltipStyle = useMemo(() => ({
    background: theme === "light" ? "#ffffff" : "#0A0A0A",
    border: `1px solid ${theme === "light" ? "#ececee" : "#27272A"}`,
    color: theme === "light" ? "#18181b" : "#ffffff",
    fontSize: 12,
    boxShadow: theme === "light" ? "0 4px 12px rgba(24,24,27,0.08)" : "none",
  }), [theme]);
  const gridStroke = theme === "light" ? "#ececee" : "#27272A";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: dash }, { data: list }] = await Promise.all([
        api.get(`/security-events/dashboard`, { params: { range } }),
        api.get(`/security-events`, { params: { limit: 25, days: range === "7d" ? 7 : range === "30d" ? 30 : 90 } }),
      ]);
      setData(dash);
      setEvents(list?.items || []);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load security overview"));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const stats = data?.stats;
  // Wrap in useMemo — `data?.timeline || []` returns a fresh literal
  // every render when data hasn't loaded, retriggering the derived memo.
  const timeline = useMemo(() => data?.timeline || [], [data]);

  // Format bucket X-axis labels for the chart based on the window size.
  const timelineData = useMemo(() => timeline.map((b) => {
    const d = new Date(b.bucket_start);
    // Daily buckets across all supported ranges (7d/30d/90d).
    const label = `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
    return { label, ...b };
  }), [timeline]);

  return (
    <div data-testid="security-dashboard-page">
      <PageHeader
        title="Security Overview"
        subtitle="Auth-adjacent events across your agency — failed logins, brute-force lockouts, password resets, and reset requests."
        action={
          <div className="flex items-center gap-2">
            <div className="flex items-center border border-[#27272A]" data-testid="security-dashboard-range">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRange(r.key)}
                  data-testid={`security-dashboard-range-${r.key}`}
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors ${
                    range === r.key
                      ? "bg-[#FF3B30] text-white"
                      : "text-zinc-400 hover:text-zinc-100 hover:bg-[#1A1A1A]"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <GhostButton onClick={load} data-testid="security-dashboard-refresh">
              <RotateCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </GhostButton>
          </div>
        }
      />

      <div className="p-8 space-y-10">
        {/* KPI tiles */}
        <section className="space-y-4" aria-labelledby="sec-kpi-heading">
          <h2 id="sec-kpi-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
            Key Metrics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KpiTile
              icon={ShieldAlert}
              label="Failed Logins"
              value={stats?.failed_logins ?? "—"}
              accent={(stats?.failed_logins || 0) > 0 ? "#FF3B30" : undefined}
              testId="sec-kpi-failed-logins"
            />
            <KpiTile
              icon={Ban}
              label="Brute-Force Lockouts"
              value={stats?.brute_force_lockouts ?? "—"}
              accent={(stats?.brute_force_lockouts || 0) > 0 ? "#FF3B30" : undefined}
              testId="sec-kpi-brute-force"
            />
            <KpiTile
              icon={KeyRound}
              label="Password Reset Requests"
              value={stats?.password_resets_requested ?? "—"}
              accent="#FF9F0A"
              testId="sec-kpi-pwd-resets"
            />
            <KpiTile
              icon={Wifi}
              label="Distinct Source IPs"
              value={stats?.unique_source_ips ?? "—"}
              accent="#0A84FF"
              testId="sec-kpi-unique-ips"
            />
            <KpiTile
              icon={Users}
              label="Targeted Accounts"
              value={stats?.unique_target_emails ?? "—"}
              accent={(stats?.unique_target_emails || 0) > 3 ? "#FF9F0A" : undefined}
              testId="sec-kpi-unique-targets"
            />
          </div>
        </section>

        {/* Timeline chart */}
        <section className="space-y-4" aria-labelledby="sec-timeline-heading">
          <h2 id="sec-timeline-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
            Failure Timeline
          </h2>
          <div className="border border-[#27272A] bg-[#121212] p-5 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timelineData} data-testid="sec-timeline-chart">
                <CartesianGrid stroke={gridStroke} vertical={false} />
                <XAxis dataKey="label" stroke="#71717A" fontSize={11} />
                <YAxis stroke="#71717A" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#FF3B3015" }} />
                <Bar dataKey="failed_logins" fill="#FF3B30" name="Failed Logins" />
                <Bar dataKey="brute_force_lockouts" fill="#FF9F0A" name="Brute-Force Blocks" />
                <Bar dataKey="password_resets" fill="#0A84FF" name="Pwd Resets" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Top offenders */}
        <section className="space-y-4" aria-labelledby="sec-top-heading">
          <h2 id="sec-top-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
            Top Offenders
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OffenderCard
              title="Source IPs"
              rows={stats?.top_source_ips || []}
              icon={Wifi}
              labelKey="ip"
              testIdPrefix="sec-top-ip"
            />
            <OffenderCard
              title="Targeted Accounts"
              rows={stats?.top_target_emails || []}
              icon={User}
              labelKey="email"
              testIdPrefix="sec-top-email"
            />
          </div>
        </section>

        {/* Recent events */}
        <section className="space-y-4" aria-labelledby="sec-events-heading">
          <h2 id="sec-events-heading" className="font-display uppercase font-bold text-2xl tracking-tight">
            Recent Events
          </h2>
          <div className="border border-[#27272A] bg-[#121212]">
            {events.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm scanlines" data-testid="sec-events-empty">
                No events recorded in this window. Nice and quiet.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="sec-events-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#27272A]">
                    <th className="py-2 px-4">Time</th>
                    <th className="py-2 px-4">Event</th>
                    <th className="py-2 px-4">Target</th>
                    <th className="py-2 px-4">IP</th>
                    <th className="py-2 px-4">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A]">
                  {events.map((e) => (
                    <tr key={e.id} data-testid={`sec-event-${e.id}`}>
                      <td className="py-2 px-4 font-mono text-xs text-zinc-400 whitespace-nowrap">
                        {fmtDateTime(e.created_at, "UTC")}
                      </td>
                      <td className="py-2 px-4">
                        <PillBadge tone={eventTone(e)}>{e.event_type}</PillBadge>
                      </td>
                      <td className="py-2 px-4 text-zinc-200 truncate max-w-[220px]" title={e.target_email}>
                        {e.target_email || "—"}
                      </td>
                      <td className="py-2 px-4 font-mono text-xs text-zinc-400">{e.ip || "—"}</td>
                      <td className="py-2 px-4 text-xs text-zinc-500 truncate max-w-[200px]" title={e.reason}>
                        {e.reason || (e.success ? "ok" : "—")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}


function KpiTile({ icon: Icon, label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="border border-[#27272A] bg-[#121212] p-5 flex flex-col gap-2"
    >
      <div className="flex items-center gap-2 label-mute">
        {Icon && <Icon className="size-3.5" />}
        {label}
      </div>
      <div
        className="font-mono font-black text-4xl tracking-tighter"
        style={{ color: accent || "#f4f4f5" }}
      >
        {value}
      </div>
    </div>
  );
}


function OffenderCard({ title, rows, icon: Icon, labelKey, testIdPrefix }) {
  return (
    <div className="border border-[#27272A] bg-[#121212]">
      <div className="px-5 py-3 border-b border-[#27272A] flex items-center gap-2">
        {Icon && <Icon className="size-4 text-[#FF3B30]" />}
        <div className="font-display uppercase font-bold text-sm">{title}</div>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-zinc-500 text-sm">
          <MailCheck className="size-6 text-[#34C759] mx-auto mb-2" />
          No repeat offenders in this window.
        </div>
      ) : (
        <ul className="divide-y divide-[#27272A]">
          {rows.map((r, i) => (
            <li
              key={r[labelKey] || i}
              data-testid={`${testIdPrefix}-${i}`}
              className="px-5 py-3 flex items-center justify-between gap-4"
            >
              <span className="font-mono text-sm text-zinc-200 truncate">{r[labelKey]}</span>
              <span className="font-mono font-bold text-[#FF3B30]">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function eventTone(e) {
  if (e.event_type === "login_failed") return "red";
  if (e.event_type === "password_reset_request") return "amber";
  if (e.event_type === "password_reset_confirm") return "green";
  if (e.event_type?.startsWith("invite_")) return "blue";
  return "default";
}
